// Обитател: „Моят дом“. Човек, който живее в имота, но НЕ е страна по наема
// (наемът се плаща от фирма или роднина). Вижда само адреса и подадените
// нередности — тук НИКОГА няма наем, задължения, депозит, документи или чат.

import React, { useCallback, useState } from 'react';
import { Image, Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, ISSUE_STATUS_LABELS } from '../../api';
import { showPhoto } from '../../photo-viewer';
import { Badge, Button, Card, COLORS, Constrain, Muted, SectionLabel, Title } from '../../ui';
import { cityLabel } from '../../geo';
import { getLocale, t } from '../../i18n';

// Същите цветове като в IssuesScreen (там не са изнесени)
const STATUS_COLORS: Record<string, string> = {
  SUBMITTED: '#D97706',
  SEEN: COLORS.primary,
  ASSIGNED: '#7C3AED',
  RESOLVED: COLORS.success,
  CONFIRMED: COLORS.muted,
};

type Home = { id: string; address: string; city: string; region?: string | null; managerName: string };

export default function ResidentHomeScreen({ navigation }: any) {
  const [apartments, setApartments] = useState<Home[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    await Promise.all([
      api('/residents/home')
        .then((r) => setApartments(r.apartments ?? []))
        .catch(() => {}),
      api('/issues')
        .then((r) => setIssues(r.issues ?? []))
        .catch(() => {}),
    ]);
  }, []);
  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: COLORS.bg }}
      contentContainerStyle={{ padding: 16, paddingBottom: 120 }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} />}
      showsVerticalScrollIndicator={false}
    >
      <Constrain>
        <Title>{t('Моят дом')}</Title>

        {apartments.length === 0 && (
          <Card>
            <Muted>{t('Още не сте добавени към имот. Когато наемодателят ви добави като обитател, адресът ще се появи тук.')}</Muted>
          </Card>
        )}

        {apartments.map((a) => (
          <Card key={a.id}>
            <Text style={{ fontSize: 17, fontWeight: '600', color: COLORS.text }}>{a.address}</Text>
            <Muted>{cityLabel(a.city, a.region)}</Muted>
            {a.managerName ? <Muted>{t('Управлява: {name}', { name: a.managerName })}</Muted> : null}
            <Button title={t('Съобщи проблем')} onPress={() => navigation.navigate('NewIssue', { apartmentId: a.id })} />
          </Card>
        ))}

        <SectionLabel style={{ marginTop: 8 }}>{t('Нередности')}</SectionLabel>
        {issues.length === 0 && (
          <Card>
            <Muted>{t('Нямате подадени нередности.')}</Muted>
          </Card>
        )}
        {issues.map((issue) => (
          <Card key={issue.id}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontWeight: '600', fontSize: 16, color: COLORS.text, flex: 1 }}>
                {issue.title || String(issue.description ?? '').slice(0, 40)}
              </Text>
              <Badge
                text={t(ISSUE_STATUS_LABELS[issue.status] ?? issue.status)}
                color={STATUS_COLORS[issue.status] ?? COLORS.primary}
              />
            </View>
            <Muted>
              {issue.apartment?.address ? `${issue.apartment.address} · ` : ''}
              {new Date(issue.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}
            </Muted>
            {issue.description ? <Text style={{ marginTop: 6, color: COLORS.text }}>{issue.description}</Text> : null}
            {Array.isArray(issue.photos) && issue.photos.length > 0 && (
              <ScrollView horizontal style={{ marginTop: 8 }} showsHorizontalScrollIndicator={false}>
                {issue.photos.map((p: string) => (
                  <Pressable key={p} onPress={() => showPhoto(`${API_URL}${p}`)}>
                    <Image
                      source={{ uri: `${API_URL}${p}` }}
                      style={{ width: 90, height: 90, borderRadius: 8, marginRight: 8 }}
                    />
                  </Pressable>
                ))}
              </ScrollView>
            )}
          </Card>
        ))}
      </Constrain>
    </ScrollView>
  );
}
